import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const languageOptions = [
  { code: "fr", label: "Français", short: "FR" },
  { code: "en", label: "English", short: "EN" },
  { code: "ar", label: "العربية", short: "عربي" },
  { code: "fa", label: "فارسی", short: "فارسی" },
];

const hiddenPaths = ["/verify", "/password-success"];

const getAuthLinkState = (path) => {
  if (path === "/signup") return { authDirection: "toSignup" };
  if (path === "/login") return { authDirection: "toLogin" };
  return undefined;
};

const Navbar = ({
  isAuthenticated,
  onLogout,
  userRole,
  userApplication,
  theme,
  onToggleTheme,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const langMenuRef = useRef(null);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const isDark = theme === "dark";
  const isAdmin = (userRole || "").toLowerCase() === "admin";
  const currentLanguageCode = (i18n.language || "fr").split("-")[0];
  const currentLanguage =
    languageOptions.find((lang) => lang.code === currentLanguageCode) || languageOptions[0];

  const brandName = useMemo(() => {
    const appName = (userApplication || "").trim();

    if (isAuthenticated && appName && appName.toLowerCase() !== "assistbyscan") {
      return appName;
    }

    return "AssistByScan";
  }, [isAuthenticated, userApplication]);

  const publicLinks = useMemo(
    () => [
      { to: "/", label: t("nav.home", "Accueil") },
      { to: "/features", label: t("nav.features", "Fonctionnalités") },
      { to: "/how-it-works", label: t("nav.howItWorks", "Comment ça marche") },
      { to: "/pricing", label: t("nav.pricing", "Tarifs") },
      { to: "/contact", label: t("nav.contact", "Contact") },
      { to: "/download", label: t("download_app", "Télécharger l'application") },
    ],
    [t]
  );

  const appLinks = useMemo(() => {
    if (isAdmin) {
      return [
        { to: "/admin-dashboard", label: t("admin_dashboard", "Tableau de bord admin") },
        { to: "/user_register_web", label: t("register_admin", "Enregistrer administrateur") },
      ];
    }

    return [
      { to: "/qr-generator", label: t("qr_generator", "Générateur QR") },
      { to: "/questions", label: t("questions", "Questions") },
      { to: "/report", label: t("report", "Rapport") },
      { to: "/static_page", label: t("static_page", "Page statique") },
      { to: "/help_page", label: t("help_center", "Centre d'aide") },
      { to: "/user_register", label: t("register_user", "Enregistrer utilisateur") },
    ];
  }, [isAdmin, t]);

  const navLinks = isAuthenticated ? appLinks : publicLinks;

  useEffect(() => {
    const lang = currentLanguage.code;
    document.documentElement.lang = lang;
    document.documentElement.dir = ["ar", "fa"].includes(lang) ? "rtl" : "ltr";
  }, [currentLanguage.code]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setLangOpen(false);
        setMobileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (hiddenPaths.includes(location.pathname)) return null;

  const isActive = (path) => location.pathname === path;

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const handleLogout = () => {
    setMobileOpen(false);
    setLangOpen(false);

    if (onLogout) {
      onLogout();
    } else {
      navigate("/login", { replace: true });
    }
  };

  const renderNavLink = (link, mobile = false) => {
    const active = isActive(link.to);
    const className = mobile
      ? `block rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted"
        }`
      : `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          active
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground"
        }`;

    return (
      <Link
        key={link.to}
        to={link.to}
        state={getAuthLinkState(link.to)}
        aria-current={active ? "page" : undefined}
        className={className}
      >
        {link.label}
      </Link>
    );
  };

  return (
    <nav className="fixed left-0 right-0 top-0 z-50 border-b border-border bg-card/80 backdrop-blur-lg">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 text-xl font-bold text-primary"
        >
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="shrink-0">
            <rect width="28" height="28" rx="6" className="fill-primary" />
            <rect x="6" y="6" width="7" height="7" rx="1" className="fill-primary-foreground" />
            <rect x="15" y="6" width="7" height="7" rx="1" className="fill-primary-foreground" />
            <rect x="6" y="15" width="7" height="7" rx="1" className="fill-primary-foreground" />
            <rect x="17" y="17" width="3" height="3" rx="0.5" className="fill-primary-foreground" />
          </svg>
          <span className="truncate">{brandName}</span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => renderNavLink(link))}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggleTheme || (() => {})}
            className="rounded-lg border border-border p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={
              isDark
                ? t("theme_light", "Activer le mode clair")
                : t("theme_dark", "Activer le mode sombre")
            }
            aria-pressed={isDark}
            title={t("darkMode.toggle", "Changer le thème")}
          >
            {isDark ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
              </svg>
            )}
          </button>

          <div className="relative" ref={langMenuRef}>
            <button
              type="button"
              onClick={() => setLangOpen((open) => !open)}
              className="rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={t("language", "Langue")}
              aria-haspopup="menu"
              aria-expanded={langOpen}
            >
              {currentLanguage.short}
            </button>

            {langOpen && (
              <div className="absolute end-0 top-full z-[60] mt-1 min-w-36 rounded-lg border border-border bg-card p-1 shadow-lg">
                {languageOptions.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    className={`block w-full rounded-md px-3 py-1.5 text-start text-xs font-medium transition-colors ${
                      currentLanguage.code === lang.code
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                    role="menuitem"
                  >
                    {lang.short}
                  </button>
                ))}
              </div>
            )}
          </div>

          {isAuthenticated ? (
            <button
              type="button"
              onClick={handleLogout}
              className="hidden rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive hover:text-destructive-foreground md:inline-flex"
            >
              {t("logout", "Déconnexion")}
            </button>
          ) : (
            <div className="hidden gap-2 md:flex">
              <Link
                to="/login"
                state={{ authDirection: "toLogin" }}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                {t("nav.login", t("nav_login", "Connexion"))}
              </Link>
              <Link
                to="/signup"
                state={{ authDirection: "toSignup" }}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                {t("nav.register", t("nav_signup", "Inscription"))}
              </Link>
            </div>
          )}

          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-muted md:hidden"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              {mobileOpen ? (
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              ) : (
                <path
                  fillRule="evenodd"
                  d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-navigation" className="border-t border-border bg-card px-4 pb-4 pt-2 md:hidden">
          {navLinks.map((link) => renderNavLink(link, true))}

          <div className="mt-3 border-t border-border pt-3">
            {isAuthenticated ? (
              <button
                type="button"
                onClick={handleLogout}
                className="w-full rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive hover:text-destructive-foreground"
              >
                {t("logout", "Déconnexion")}
              </button>
            ) : (
              <div className="flex flex-col gap-2">
                <Link
                  to="/login"
                  state={{ authDirection: "toLogin" }}
                  className="rounded-lg border border-border px-3 py-2.5 text-center text-sm font-medium text-foreground"
                >
                  {t("nav.login", t("nav_login", "Connexion"))}
                </Link>
                <Link
                  to="/signup"
                  state={{ authDirection: "toSignup" }}
                  className="rounded-lg bg-primary px-3 py-2.5 text-center text-sm font-medium text-primary-foreground"
                >
                  {t("nav.register", t("nav_signup", "Inscription"))}
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
